// Based on LmFormFieldContainer from @tamagui-extras/form
// https://github.com/dohomi/tamagui-kitchen-sink/blob/master/packages/form/src/LmFormFieldContainer.tsx

import {
  Label,
  LabelProps,
  SizableText,
  SizableTextProps,
  ThemeableStackProps,
  YStack,
  useThemeName,
} from "tamagui";

export interface FormFieldContainerBaseProps {
  /**
   * If `true`, the form field will be marked as required.
   */
  required?: boolean;
  /**
   * If `true`, the form field will be marked as having an error.
   */
  error?: boolean;
  /**
   * The text to display as the label for the form field.
   */
  label?: string;
  /**
   * Props to customize the appearance and behavior of the label.
   */
  labelProps?: Omit<LabelProps, "size">;
  /**
   * The helper text to display below the form field.
   */
  helperText?: string;
  /**
   * Props to customize the appearance and behavior of the helper text.
   */
  helperTextProps?: Omit<SizableTextProps, "size">;
  /**
   * Props to customize the appearance and behavior of the component that wraps the form field.
   */
  containerProps?: Omit<ThemeableStackProps, "size">;
}

export interface FormFieldContainerProps
  extends ThemeableStackProps,
    Omit<FormFieldContainerBaseProps, "containerProps"> {}

export const FormFieldContainer = ({
  required,
  error,
  label,
  labelProps,
  helperText,
  helperTextProps,
  children,
  ...rest
}: FormFieldContainerProps) => {
  const themeName = useThemeName();

  return (
    <YStack gap="$1.5" theme={error ? "red" : themeName} {...rest}>
      {label && (
        <Label {...labelProps}>
          {label}
          {required && (
            <SizableText theme="red" color="$color9">
              *
            </SizableText>
          )}
        </Label>
      )}
      {children}
      {helperText && (
        <SizableText
          fontSize="$3"
          color="$color10"
          paddingVertical="$1.5"
          {...helperTextProps}
        >
          {helperText}
        </SizableText>
      )}
    </YStack>
  );
};
